/**
 * VulnerabilitySearchStep Component
 *
 * Step for searching and selecting vulnerabilities (risks).
 * Used as step 1 in the Vulnerability wizard.
 *
 * Features:
 * - TanStack Table with severity sorting using SelectionTable
 * - Prepopulated with first 200 risks (severity-sorted)
 * - Client-side filtering as user types
 * - Custom vulnerability creation via "Add custom" option
 * - Input sanitization for security
 */

import { useCallback, useMemo, useState } from 'react';

import {
  vulnerabilityColumns,
  vulnerabilityDefaultSort,
} from '@/sections/attacks/attackPaths/components/wizards/columns';
import {
  AddCustomOption,
  CurrentlyEditingDisplay,
  SearchEmptyState,
  SelectionTable,
  WizardSearchInput,
} from '@/sections/attacks/attackPaths/components/wizards/components';
import {
  type SearchableListItem,
  useSearchableList,
} from '@/sections/attacks/attackPaths/components/wizards/hooks';
import type { VulnerabilityWizardFormData } from '@/sections/attacks/attackPaths/components/wizards/types';

/**
 * Props for VulnerabilitySearchStep component
 */
interface VulnerabilitySearchStepProps {
  /** Current wizard form data */
  formData: VulnerabilityWizardFormData;
  /** Form data setter */
  setFormData: (
    updater:
      | VulnerabilityWizardFormData
      | ((prev: VulnerabilityWizardFormData) => VulnerabilityWizardFormData)
  ) => void;
  /**
   * Optional preselected vulnerability key for edit mode.
   * When provided, ensures this vulnerability is included in results
   * even if it would be outside the pagination window.
   */
  preselectedKey?: string;
}

/**
 * Vulnerability search step for Vulnerability wizard.
 *
 * Provides a search input with TanStack Table for selecting existing Chariot risks
 * or adding custom vulnerabilities.
 */
export function VulnerabilitySearchStep({
  formData,
  setFormData,
  preselectedKey,
}: VulnerabilitySearchStepProps) {
  const [searchValue, setSearchValue] = useState('');

  // Fetch and filter data
  const { items, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useSearchableList({
      type: 'risk',
      searchTerm: searchValue,
      // Pass preselectedKey to ensure the selected vulnerability is always in the list (for edit mode auto-scroll)
      preselectedKey,
    });

  // Derive metadata from loaded items for edit mode display
  const selectedItemMetadata = useMemo(() => {
    if (!preselectedKey || !items.length) return formData.vulnerabilityMetadata;
    const selectedItem = items.find(item => item.key === preselectedKey);
    return selectedItem?.metadata ?? formData.vulnerabilityMetadata;
  }, [preselectedKey, items, formData.vulnerabilityMetadata]);

  /**
   * Handle search change
   */
  const handleSearchChange = (value: string) => {
    setSearchValue(value);
  };

  /**
   * Handle vulnerability selection from table
   */
  const handleSelect = useCallback(
    (item: SearchableListItem) => {
      const priority = item.priority;
      setFormData(prev => ({
        ...prev,
        vulnerabilityKey: item.key,
        vulnerabilityName: item.name,
        vulnerabilityMetadata: item.metadata,
        priority,
        isCustom: false,
        // Auto-populate affected asset from vulnerability's metadata (asset identifier)
        affectedAssetId: item.metadata || '',
        isAffectedAssetCustom: false,
      }));
      setSearchValue(''); // Clear search after selection
    },
    [setFormData]
  );

  /**
   * Handle custom vulnerability entry
   */
  const handleAddCustom = useCallback(
    (term: string) => {
      setFormData(prev => ({
        ...prev,
        isCustom: true,
        vulnerabilityKey: term,
        vulnerabilityName: term,
      }));
      setSearchValue('');
    },
    [setFormData]
  );

  // Dynamic row count: 8 rows in Add mode, 6 in Edit mode
  const isEditMode = Boolean(preselectedKey);
  const tableMaxRows = isEditMode ? 6 : 8;

  return (
    <div
      className="flex flex-col gap-4"
      data-testid="vulnerability-search-step"
    >
      {/* Currently Editing Display (Edit Mode Only) */}
      {preselectedKey && formData.vulnerabilityKey && !formData.isCustom && (
        <CurrentlyEditingDisplay
          name={formData.vulnerabilityName || formData.vulnerabilityKey}
          metadata={selectedItemMetadata}
          status={undefined}
          isCustom={false}
        />
      )}

      {/* Currently Selected Custom Vulnerability */}
      {formData.isCustom && formData.vulnerabilityKey && (
        <CurrentlyEditingDisplay
          name={formData.vulnerabilityName || formData.vulnerabilityKey}
          isCustom={true}
        />
      )}

      <p
        id="vulnerability-search-description"
        className="text-chariot-text-secondary text-sm"
      >
        Search for an existing Chariot vulnerability or add a custom one to
        include in the attack path.
      </p>

      {/* Search Input */}
      <WizardSearchInput
        value={searchValue}
        onChange={handleSearchChange}
        placeholder="Search for a vulnerability..."
        ariaLabel="Search vulnerabilities"
        ariaDescribedBy="vulnerability-search-description"
        testId="vulnerability-search-input"
        autoFocus
      />

      {/* Add Custom Option */}
      <AddCustomOption
        searchTerm={searchValue}
        onAddCustom={handleAddCustom}
        entityName="vulnerability"
        testIdPrefix="vulnerability-add-custom"
      />

      {/* Results Table or Empty State */}
      {items.length === 0 && searchValue.trim() ? (
        <SearchEmptyState
          searchTerm={searchValue}
          onClearSearch={() => setSearchValue('')}
          entityName="vulnerabilities"
          testIdPrefix="vulnerability-search-empty"
        />
      ) : (
        <SelectionTable
          data={items}
          columns={vulnerabilityColumns}
          selectedKey={formData.isCustom ? null : formData.vulnerabilityKey}
          onSelect={handleSelect}
          isLoading={isLoading}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          onLoadMore={fetchNextPage}
          defaultSort={vulnerabilityDefaultSort}
          maxRows={tableMaxRows}
          highlightSelected={!preselectedKey}
        />
      )}
    </div>
  );
}
